import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { FarmersSegment, TableWrapper } from './style'
import { Dimmer, Dropdown, Input, Loader, Popup, SemanticICONS } from 'semantic-ui-react'
import TableComponent from '~/components/TableComponent'
import CO2ClimateQuery from '~/QueryHooks/CO2ClimateQuery'
import { useDebounce } from 'use-lodash-debounce';
import CustomIcon from '~/components/CustomIcon'
import { DownloadService } from '~/api/services/downloadFiles'
import { handleErrors } from '~/utils/helpers'
import i18n from '~/components/i18n'
import CO2Service, { FarmerCo2Type } from '~/api/services/co2'
import { Nullable } from '~/types'
import ResetModal from './ResetModal'
import RecalculateModal from './RecalculateModal'
import notify from '~/components/Notify'

interface Props {
  farmerGroupId: string
  isLive: boolean
}

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    sorted: 'desc'
  },
  {
    name: i18n.t('Company name'),
    key: 'companyName',
    sorted: 'desc'
  },
  {
    name: i18n.t('Progress'),
    key: 'percentage',
    type: 'clima-progress',
    sorted: 'desc'
  },
  {
    name: i18n.t('Status'),
    key: 'completed',
    type: 'verified'
  },
  {
    name: i18n.t('Last login'),
    key: 'lastLogin',
    type: 'date',
    format: 'DD.MM.YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Completed at'),
    key: 'completedAt',
    type: 'date',
    format: 'dddd, D MMMM YYYY HH:mm',
    sorted: 'desc'
  },
  {
    name: i18n.t('Emission factor'),
    key: 'soldMilkEconomicAllocationFpcmIdf',
    type: 'number-or-value',
    unit: 'kg CO2e / kg FPCM',
    decimalScale: 2,
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const FarmersTable: React.FC<Props> = ({ farmerGroupId, isLive }) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const actions = [
    {
      popupMessage: <span dangerouslySetInnerHTML={{ __html: t('CO2 emission result') }} />,
      type: 'icon',
      name: 'file pdf',
      size: 'large',
      actionType: 'pdf-results',
      renderActions: getActions
    },
    {
      popupMessage: 'Datenerhebung zurücksetzen',
      type: 'icon',
      name: 'repeat',
      size: 'large',
      actionType: 'reset',
      renderActions: getActions
    },
    {
      popupMessage: 'Datenerhebung. neu berechnen',
      type: 'icon',
      name: 'sync',
      size: 'large',
      actionType: 'recalculate',
      renderActions: getActions
    }
  ]

  const completedFilterOpt = [
    {
      text: t('Data collection completed'),
      value: 1
    },
    {
      text: t('Data collection not completed'),
      value: 0
    }
  ]

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })
  const [search, setSearch] = useState('')
  const [resetFarmerData, setResetFarmerData] = useState<Nullable<FarmerCo2Type>>(null)
  const [recalculateFarmerData, setRecalculateFarmerData] = useState<Nullable<FarmerCo2Type>>(null)
  const debouncedSearchValue = useDebounce(search, 300)
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    search: '',
    farmerGroupId,
    completed: null
  });

  const {
    isLoading: isBusyFarmers,
    data: farmers,
    refetch: fetchFilteredData,
    isFetching: isFetchingData
  } = CO2ClimateQuery.getFarmers(
    id,
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
      farmerGroupId
    });

  useEffect(() => {
    if (farmerGroupId) {
      fetchFilteredData()
    }
  }, [farmerGroupId])

  useEffect(() => {
    fetchFilteredData();
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedSearchValue, page: 0 });
  }, [debouncedSearchValue]);

  const { filtered, rows } = farmers ? farmers : { filtered: 0, rows: [] };

  async function handleDownloadPdfResults(farmer) {
    setLoadingAction({
      type: 'pdf-results',
      id: farmer.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileWithName(`co2-footprints/${id}/farmers/${farmer?.id}/get-results/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function getActions(farmer, actionType) {
    const action = actions.find(action => action.actionType === actionType)
    const displayResetAction = isLive && farmer.percentage > 0
    const displayRecalculateAction = farmer?.completed === 1
    switch (actionType) {
      case 'pdf-results':
        return farmer?.completed === 1 && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => handleDownloadPdfResults(farmer)}
            size='large'
          />}
        />
      case 'reset':
        return displayResetAction && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => setResetFarmerData(farmer)}
            size='large'
          />}
        />
      case 'recalculate':
        return displayRecalculateAction && <Popup
          content={action.popupMessage}
          trigger={<CustomIcon
            style={{ cursor: 'pointer' }} name={action.name as SemanticICONS}
            onClick={() => setRecalculateFarmerData(farmer)}
            size='large'
          />}
        />
      default:
        return
    }
  }

  async function handleRecalculateData() {
    setLoadingAction({
      type: 'recalculate',
      id: recalculateFarmerData.id,
      value: true,
      idKey: 'id'
    })
    try {
      await CO2Service.recalculateFarmersCo2Slot(id, recalculateFarmerData.id)
      fetchFilteredData()
      notify('Die Datenerfassung wurde erfolgreich neu berechnet.', 'success')

    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function renderResetModal() {
    return <ResetModal
      handleClose={(submited?: boolean) => {
        setResetFarmerData(null)
        if (submited) {
          fetchFilteredData()
        }
      }}
      farmersData={resetFarmerData}
    />
  }

  function renderRecalculateModal() {
    return <RecalculateModal
      handleClose={(submited?: boolean) => {
        setRecalculateFarmerData(null)
        if (submited) {
          handleRecalculateData()
        }
      }}
      farmersData={recalculateFarmerData}
    />
  }

  return <FarmersSegment>
    <div className='table-header'>
      <div>{t('Farmers')}</div>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        selectOnBlur={false}
        disabled={isBusyFarmers}
        options={completedFilterOpt}
        selection
        placeholder={t('Data collection completed?')}
        clearable
        value={dataFiltered.completed}
        onChange={(e, data) => {
          setDataFiltered({
            ...dataFiltered,
            //@ts-ignore per backend request
            completed: isNaN(data.value) ? null : data.value
          })
        }}
      />
    </div>
    <TableWrapper>
      {renderResetModal()}
      {renderRecalculateModal()}
      <Dimmer inverted active={isBusyFarmers}>
        <Loader active={isBusyFarmers} />
      </Dimmer>
      <TableComponent
        tableColumnHeaders={tableColumnHeaders}
        animated
        loadingAction={loadingAction}
        actions={actions}
        isFetchingData={isFetchingData}
        dataFiltered={dataFiltered}
        handleChangeDataFiltered={(data, tableColumnHeaders) => {
          setTableColumnHeaders(tableColumnHeaders)
          setDataFiltered(data)
        }}
        rows={rows}
        total={filtered}
      />
    </TableWrapper>
  </FarmersSegment>
}
export default FarmersTable